/* eslint-disable react-hooks/exhaustive-deps -- effects intentionally run on mount / on the listed deps; adding the flagged functions risks render loops. */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Chip,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Tooltip,
  Skeleton,
  Backdrop,
  CircularProgress,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  FilterAltOff as FilterAltOffIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  AddAPhoto as AddAPhotoIcon,
} from '@mui/icons-material';
import { wardrobeAPI, searchAPI, uploadToPresignedUrl, triggerDownload } from '../services/api';
import { WardrobeItem } from '../types';
import ItemImageGallery from '../components/ItemImageGallery';
import ImageLightbox, { LightboxImage } from '../components/ImageLightbox';
import { useUI } from '../contexts/UIContext';

const Wardrobe: React.FC = () => {
  const { toast } = useUI();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState<WardrobeItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [styleFilter, setStyleFilter] = useState('all');
  const [colorFilter, setColorFilter] = useState('all');
  const [seasonFilter, setSeasonFilter] = useState('all');
  const [favoritesFilter, setFavoritesFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [useSemanticSearch, setUseSemanticSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WardrobeItem | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [itemUsage, setItemUsage] = useState<{ outfits: any[]; collections: any[]; fit_history: any[] } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<WardrobeItem>>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<LightboxImage[]>([]);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPhase, setImportPhase] = useState<'uploading' | 'processing' | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchItems();
  }, []);

  // Apply filters from URL query params (e.g. /wardrobe?filter=favorites)
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'favorites') setFavoritesFilter('favorites');
    if (filter === 'unworn') setSortBy('wear_count_asc');
    const action = searchParams.get('action');
    if (action === 'add') navigate('/upload');
  }, [searchParams, navigate]);

  useEffect(() => {
    // Use semantic search for natural language queries, otherwise use standard filtering
    if (searchQuery && useSemanticSearch) {
      handleSemanticSearch();
    } else {
      filterAndSortItems();
    }
  }, [items, searchQuery, categoryFilter, styleFilter, colorFilter, seasonFilter, favoritesFilter, sortBy, useSemanticSearch]);

  const fetchItems = async () => {
    try {
      const response = await wardrobeAPI.getItems();
      setItems(response.data);
      setFilteredItems(response.data);
    } catch (error) {
      console.error('Error fetching wardrobe items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await wardrobeAPI.exportWardrobe();
      const { download_url, filename, counts, warnings } = response.data;
      triggerDownload(download_url, filename);
      const summary = `Exported ${counts.items} items, ${counts.outfits} outfits, ${counts.collections} collections and ${counts.images} images.`;
      if (warnings && warnings.length > 0) {
        toast(`${summary} ${warnings.length} image(s) could not be included.`, 'warning');
      } else {
        toast(summary, 'success');
      }
    } catch (error) {
      console.error('Error exporting wardrobe:', error);
      toast('Failed to export wardrobe. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so selecting the same file again still fires onChange.
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast('Please choose a wardrobe export (.zip) file.', 'error');
      return;
    }

    setImporting(true);
    try {
      // 1. Ask the backend for a presigned URL, 2. upload the file straight to
      // S3, 3. tell the backend to process the uploaded archive.
      setImportPhase('uploading');
      const { data: uploadInfo } = await wardrobeAPI.getImportUploadUrl();
      await uploadToPresignedUrl(uploadInfo.upload_url, file);
      setImportPhase('processing');
      const { data: result } = await wardrobeAPI.importWardrobe(uploadInfo.s3_key);

      const imp = result.imported;
      const summary = `Imported ${imp.items} items, ${imp.outfits} outfits and ${imp.collections} collections.`;
      if (result.warnings && result.warnings.length > 0) {
        toast(`${summary} ${result.warnings.length} warning(s).`, 'warning');
      } else {
        toast(summary, 'success');
      }
      await fetchItems();
    } catch (error) {
      console.error('Error importing wardrobe:', error);
      toast('Failed to import wardrobe. Make sure the file is a valid export.', 'error');
    } finally {
      setImporting(false);
      setImportPhase(null);
    }
  };

  const handleSemanticSearch = async () => {
    if (!searchQuery.trim()) {
      filterAndSortItems();
      return;
    }

    setIsSearching(true);
    try {
      const response = await searchAPI.semanticSearch({
        query: searchQuery,
        include_outfits: false,
        limit: 50
      });

      if (response.data && response.data.items) {
        // Extract items from semantic search results
        const semanticItems = response.data.items.map((result: any) => ({
          ...result,
          similarity_score: result.similarity_score
        }));

        // Apply additional filters
        let filtered = semanticItems;

        if (categoryFilter !== 'all') {
          filtered = filtered.filter((item: WardrobeItem) => item.category === categoryFilter);
        }
        if (styleFilter !== 'all') {
          filtered = filtered.filter((item: WardrobeItem) => item.style === styleFilter);
        }
        if (colorFilter !== 'all') {
          filtered = filtered.filter((item: WardrobeItem) =>
            item.colors?.some(color => color.toLowerCase() === colorFilter.toLowerCase())
          );
        }
        if (seasonFilter !== 'all') {
          filtered = filtered.filter((item: WardrobeItem) =>
            item.season?.some(season => season.toLowerCase() === seasonFilter.toLowerCase())
          );
        }
        if (favoritesFilter === 'favorites') {
          filtered = filtered.filter((item: WardrobeItem) => item.is_favorite);
        }

        setFilteredItems(filtered);
      }
    } catch (error) {
      console.error('Semantic search failed:', error);
      // Fall back to standard filtering
      filterAndSortItems();
    } finally {
      setIsSearching(false);
    }
  };

  const filterAndSortItems = () => {
    let filtered = [...items];

    // Text search - searches across multiple fields
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.brand?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query) ||
        item.subcategory?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        item.colors?.some(color => color.toLowerCase().includes(query)) ||
        item.materials?.some(material => material.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Style filter
    if (styleFilter !== 'all') {
      filtered = filtered.filter(item => item.style === styleFilter);
    }

    // Color filter
    if (colorFilter !== 'all') {
      filtered = filtered.filter(item =>
        item.colors?.some(color => color.toLowerCase() === colorFilter.toLowerCase())
      );
    }

    // Season filter
    if (seasonFilter !== 'all') {
      filtered = filtered.filter(item =>
        item.season?.some(season => season.toLowerCase() === seasonFilter.toLowerCase())
      );
    }

    // Favorites filter
    if (favoritesFilter === 'favorites') {
      filtered = filtered.filter(item => item.is_favorite);
    }

    // Sorting
    switch (sortBy) {
      case 'date_desc':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'date_asc':
        filtered.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'name_asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'wear_count_desc':
        filtered.sort((a, b) => b.wear_count - a.wear_count);
        break;
      case 'wear_count_asc':
        filtered.sort((a, b) => a.wear_count - b.wear_count);
        break;
      case 'last_worn':
        filtered.sort((a, b) => {
          if (!a.last_worn) return 1;
          if (!b.last_worn) return -1;
          return new Date(b.last_worn).getTime() - new Date(a.last_worn).getTime();
        });
        break;
      case 'favorites':
        filtered.sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0));
        break;
    }

    setFilteredItems(filtered);
  };

  const handleToggleFavorite = async (item: WardrobeItem) => {
    try {
      await wardrobeAPI.updateItem(item.id, { is_favorite: !item.is_favorite });
      fetchItems();
    } catch (error) {
      console.error('Error updating favorite status:', error);
    }
  };

  const handleDelete = (itemId: number) => {
    const victim = items.find(i => i.id === itemId);
    if (!victim) return;

    // Optimistically remove
    setItems(prev => prev.filter(i => i.id !== itemId));
    setFilteredItems(prev => prev.filter(i => i.id !== itemId));

    let cancelled = false;
    const commit = async () => {
      if (cancelled) return;
      try {
        await wardrobeAPI.deleteItem(itemId);
      } catch (error: any) {
        console.error('Error deleting item:', error);
        toast(error.response?.data?.error || 'Failed to delete item. Restoring.', 'error');
        setItems(prev => [victim, ...prev]);
      }
    };

    const timer = setTimeout(commit, 5000);

    toast(`Deleted "${victim.name}"`, 'success', {
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => {
          cancelled = true;
          clearTimeout(timer);
          setItems(prev => (prev.some(i => i.id === victim.id) ? prev : [victim, ...prev]));
        },
      },
    });
  };

  const handleRecordWear = async (itemId: number) => {
    try {
      await wardrobeAPI.recordWear(itemId);
      fetchItems();
    } catch (error) {
      console.error('Error recording wear:', error);
    }
  };

  const handleViewDetails = (item: WardrobeItem) => {
    setSelectedItem(item);
    setDetailsOpen(true);
    setItemUsage(null);
    wardrobeAPI.getItemUsage(item.id)
      .then((res) => setItemUsage(res.data))
      .catch(() => setItemUsage(null));
  };

  const handleOpenEdit = (item: WardrobeItem) => {
    setSelectedItem(item);
    setEditFormData({
      name: item.name,
      category: item.category,
      subcategory: item.subcategory || '',
      brand: item.brand || '',
      size: item.size || '',
      colors: item.colors || [],
      materials: item.materials || [],
      fit: item.fit || '',
      style: item.style || '',
      season: item.season || [],
      occasion: item.occasion || [],
      tags: item.tags || [],
      care_instructions: item.care_instructions || '',
      description: item.description || '',
      notes: item.notes || '',
      purchase_price: item.purchase_price,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;

    try {
      await wardrobeAPI.updateItem(selectedItem.id, editFormData);
      setEditOpen(false);
      fetchItems();
      toast('Item updated', 'success');
    } catch (error) {
      console.error('Error updating item:', error);
      toast('Failed to update item. Please try again.', 'error');
    }
  };

  const handleEditFormChange = (field: string, value: any) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const categories = ['all', ...Array.from(new Set(items.map(item => item.category)))];
  const styles = ['all', ...Array.from(new Set(items.map(item => item.style).filter(Boolean)))];
  const allColors = Array.from(new Set(items.flatMap(item => item.colors || []))).sort();
  const colors = ['all', ...allColors];

  // Wardrobe card framing.
  // Pre-cropped thumbnails from the backend (`thumbnail_url`) are already
  // centered on the garment by Rekognition + PIL, so `cover` gives a clean,
  // consistent grid. For items without a smart thumb yet (pre-backfill or
  // failures), fall back to the full image with `contain` — uglier but the
  // whole item is at least visible.
  const thumbStyle = (item: WardrobeItem) =>
    item.thumbnail_url
      ? { objectFit: 'cover' as const, objectPosition: 'center' as const }
      : { objectFit: 'contain' as const, objectPosition: 'center' as const };
  const allSeasons = Array.from(new Set(items.flatMap(item => item.season || []))).sort();
  const seasons = ['all', ...allSeasons];

  const activeFilterCount = [categoryFilter, styleFilter, colorFilter, seasonFilter, favoritesFilter]
    .filter((v) => v && v !== 'all').length + (searchQuery ? 1 : 0);

  const resetFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
    setStyleFilter('all');
    setColorFilter('all');
    setSeasonFilter('all');
    setFavoritesFilter('all');
    setSortBy('date_desc');
  };

  // Sort + filter selects, reused inline (desktop) and in the mobile sheet (vertical)
  const renderFilterFields = (fullWidth: boolean) => {
    const fcSx = fullWidth ? { width: '100%' } : { minWidth: 140, flexShrink: 0 };
    return (
      <>
        <FormControl size="small" sx={fcSx}>
          <InputLabel>Sort By</InputLabel>
          <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)} disabled={useSemanticSearch}>
            <MenuItem value="date_desc">Newest First</MenuItem>
            <MenuItem value="date_asc">Oldest First</MenuItem>
            <MenuItem value="name_asc">Name (A-Z)</MenuItem>
            <MenuItem value="name_desc">Name (Z-A)</MenuItem>
            <MenuItem value="wear_count_desc">Most Worn</MenuItem>
            <MenuItem value="wear_count_asc">Least Worn</MenuItem>
            <MenuItem value="last_worn">Recently Worn</MenuItem>
            <MenuItem value="favorites">Favorites First</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={fcSx}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
            {categories.map(cat => <MenuItem key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={fcSx}>
          <InputLabel>Style</InputLabel>
          <Select value={styleFilter} label="Style" onChange={(e) => setStyleFilter(e.target.value)}>
            {styles.map(style => <MenuItem key={style} value={style}>{style === 'all' ? 'All Styles' : style}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={fcSx}>
          <InputLabel>Color</InputLabel>
          <Select value={colorFilter} label="Color" onChange={(e) => setColorFilter(e.target.value)}>
            {colors.map(color => <MenuItem key={color} value={color}>{color === 'all' ? 'All Colors' : color}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={fcSx}>
          <InputLabel>Season</InputLabel>
          <Select value={seasonFilter} label="Season" onChange={(e) => setSeasonFilter(e.target.value)}>
            {seasons.map(season => <MenuItem key={season} value={season}>{season === 'all' ? 'All Seasons' : season}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={fcSx}>
          <InputLabel>Favorites</InputLabel>
          <Select value={favoritesFilter} label="Favorites" onChange={(e) => setFavoritesFilter(e.target.value)}>
            <MenuItem value="all">All Items</MenuItem>
            <MenuItem value="favorites">Favorites Only</MenuItem>
          </Select>
        </FormControl>
      </>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Backdrop open={importing} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" />
        <Typography variant="h6">
          {importPhase === 'uploading' ? 'Uploading your file…' : 'Validating & importing your wardrobe…'}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          This may take a moment. Please keep this tab open.
        </Typography>
      </Backdrop>
      <Box className="fade-in-up" sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
            {filteredItems.length} of {items.length} pieces
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5 }}>My Wardrobe</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <input
            ref={importInputRef}
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Tooltip title="Download all items, outfits, collections and images as a .zip">
            <span>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={exporting || importing}
              >
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Import items, outfits and collections from a wardrobe export (.zip)">
            <span>
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                onClick={() => importInputRef.current?.click()}
                disabled={exporting || importing}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/upload')}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Filters - sticky */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          position: 'sticky',
          top: 64,
          zIndex: 5,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, flexWrap: 'wrap', alignItems: 'center', mb: { xs: 0, md: 2 } }}>
          <TextField
            label={useSemanticSearch ? "Natural Language Search (powered by AI)" : "Search (name, brand, color, material...)"}
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: { xs: '100%', md: 300 } }}
            placeholder={useSemanticSearch ? "e.g., show me comfy clothes for a casual weekend" : "e.g., blue cotton dress, Nike sneakers"}
            InputProps={{
              endAdornment: isSearching ? (
                <Typography variant="caption" color="primary">Searching...</Typography>
              ) : null
            }}
          />
          <Button
            variant={useSemanticSearch ? "contained" : "outlined"}
            size="small"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setUseSemanticSearch(!useSemanticSearch)}
            sx={{ flexShrink: 0 }}
          >
            {useSemanticSearch ? 'AI Search' : 'Standard'}
          </Button>
          {isMobile && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterSheetOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
            </Button>
          )}
        </Box>

        {/* Desktop: filters laid out inline. Mobile: in the bottom sheet below. */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {renderFilterFields(false)}
            <Chip
              label={`${filteredItems.length} of ${items.length} items`}
              color="primary"
              variant="outlined"
              sx={{ height: 40 }}
            />
            {activeFilterCount > 0 && (
              <Tooltip title="Clear all filters">
                <Button size="small" startIcon={<FilterAltOffIcon />} onClick={resetFilters} sx={{ color: 'text.secondary' }}>
                  Reset
                </Button>
              </Tooltip>
            )}
          </Box>
        )}
      </Paper>

      {/* Mobile filter & sort bottom sheet */}
      <Drawer
        anchor="bottom"
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2, pb: 'calc(16px + env(safe-area-inset-bottom))', maxHeight: '85vh' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Filter &amp; sort</Typography>
          {activeFilterCount > 0 && (
            <Button size="small" startIcon={<FilterAltOffIcon />} onClick={resetFilters}>Reset</Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {renderFilterFields(true)}
        </Box>
        <Button fullWidth variant="contained" size="large" sx={{ mt: 2 }} onClick={() => setFilterSheetOpen(false)}>
          Show {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
        </Button>
      </Drawer>

      {/* Items Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Box>
                <Skeleton variant="rectangular" height={220} animation="wave" />
                <Box sx={{ p: 2 }}>
                  <Skeleton variant="text" width="70%" height={22} />
                  <Skeleton variant="text" width="50%" height={16} />
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                    <Skeleton variant="rectangular" width={50} height={22} />
                    <Skeleton variant="rectangular" width={50} height={22} />
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : filteredItems.length === 0 ? (
        <Paper className="fade-in" sx={{ p: 6, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 56, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {items.length === 0 ? 'Nothing here yet' : 'No items match these filters'}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {items.length === 0
              ? 'Add your first piece to start building your wardrobe.'
              : 'Try clearing filters or search a different term.'}
          </Typography>
          {items.length === 0 ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/upload')}>
              Add Your First Item
            </Button>
          ) : (
            <Button variant="outlined" startIcon={<FilterAltOffIcon />} onClick={resetFilters}>
              Clear Filters
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          {filteredItems.map((item, idx) => (
            <Grid
              size={{ xs: 6, sm: 6, md: 4, lg: 3 }}
              key={item.id}
              sx={{
                opacity: 0,
                animation: 'fadeInUp 0.5s ease-out forwards',
                animationDelay: `${Math.min(idx, 12) * 0.04}s`,
              }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  '&:hover .card-overlay-actions': { opacity: 1, transform: 'translateY(0)' },
                  '&:hover .card-image': { transform: 'scale(1.06)' },
                }}
              >
                {(item.thumbnail_url || item.image_url) && (
                  <Box
                    sx={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
                    onClick={() => {
                      // Lightbox always opens the FULL original image, even
                      // when the grid shows a cropped thumbnail.
                      setLightboxImages([{
                        src: item.image_url || item.thumbnail_url!,
                        alt: item.name,
                        caption: item.name,
                        subcaption: [item.brand, item.category].filter(Boolean).join(' · '),
                      }]);
                      setLightboxOpen(true);
                    }}
                  >
                    <CardMedia
                      component="img"
                      className="card-image"
                      image={item.thumbnail_url || item.image_url}
                      alt={item.name}
                      sx={{
                        width: '100%',
                        height: { xs: 150, sm: 200, md: 220 },
                        ...thumbStyle(item),
                        backgroundColor: '#f5f5f5',
                        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                    {item.is_favorite && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'rgba(196, 30, 58, 0.95)',
                          color: '#fff',
                          px: 0.75,
                          py: 0.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        <FavoriteIcon sx={{ fontSize: 12 }} /> Favorite
                      </Box>
                    )}
                    {/* "Replace photo" hint: shown when the backend couldn't
                        smart-crop a thumbnail (Rekognition found no garment in
                        the source photo — likely obscured by hand, weird
                        angle, etc). Clicking it jumps to the edit dialog
                        where the user can add a clearer image. */}
                    {!item.thumbnail_url && item.image_url && (
                      <Box
                        onClick={(e) => { e.stopPropagation(); handleOpenEdit(item); }}
                        title="Tap to upload a clearer photo of this item"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(212, 175, 55, 0.95)',
                          color: '#1A1A1A',
                          px: 0.75,
                          py: 0.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 600,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(212, 175, 55, 1)' },
                        }}
                      >
                        <AddAPhotoIcon sx={{ fontSize: 12 }} /> Reshoot
                      </Box>
                    )}
                    <Box
                      className="card-overlay-actions"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)',
                        opacity: 0,
                        transform: 'translateY(8px)',
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                        display: 'flex',
                        gap: 0.5,
                        p: 1,
                        justifyContent: 'flex-end',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Quick view">
                        <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleViewDetails(item)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleOpenEdit(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={item.is_favorite ? 'Unfavorite' : 'Favorite'}>
                        <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleToggleFavorite(item)}>
                          {item.is_favorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" sx={{ color: '#fff' }} onClick={() => handleDelete(item.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    noWrap
                    sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, lineHeight: 1.25, fontWeight: 600, mb: 0.25 }}
                  >
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.72rem', sm: '0.875rem' } }}>
                    {item.brand || 'No brand'} • {item.category}
                    {item.subcategory && ` • ${item.subcategory}`}
                  </Typography>
                  {/* Description + color chips add clutter on small 2-up cards; show on sm+ (always in the details view) */}
                  {item.description && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
                      {item.description.length > 100 ? `${item.description.substring(0, 100)}...` : item.description}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1, display: { xs: 'none', sm: 'block' } }}>
                    {item.colors?.slice(0, 3).map((color) => (
                      <Chip key={color} label={color} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: { xs: 0.5, sm: 1 } }}>
                    Worn {item.wear_count} {item.wear_count === 1 ? 'time' : 'times'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        {selectedItem && (
          <>
            <DialogTitle>{selectedItem.name}</DialogTitle>
            <DialogContent>
              {/* Image Gallery - Full Width */}
              {selectedItem.images && selectedItem.images.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <ItemImageGallery
                    itemId={selectedItem.id}
                    images={selectedItem.images}
                    primaryImageId={selectedItem.primary_image_id}
                    onImagesUpdate={() => {}}
                    editable={false}
                  />
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  {selectedItem.description && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        AI Summary
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {selectedItem.description}
                      </Typography>
                    </Box>
                  )}
                  {selectedItem.notes && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {selectedItem.notes}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Category:</strong> {selectedItem.category}
                    {selectedItem.subcategory && ` / ${selectedItem.subcategory}`}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Brand:</strong> {selectedItem.brand || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Size:</strong> {selectedItem.size || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Fit:</strong> {selectedItem.fit || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Style:</strong> {selectedItem.style || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Colors:</strong> {selectedItem.colors?.join(', ') || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Materials:</strong> {selectedItem.materials?.join(', ') || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Seasons:</strong> {selectedItem.season?.join(', ') || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Occasions:</strong> {selectedItem.occasion?.join(', ') || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Care Instructions:</strong> {selectedItem.care_instructions || 'N/A'}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Wear Count:</strong> {selectedItem.wear_count}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Last Worn:</strong> {selectedItem.last_worn || 'Never'}
                  </Typography>
                  {selectedItem.ocr_text && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Detected Text (OCR)
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                        {selectedItem.ocr_text}
                      </Typography>
                    </Box>
                  )}

                  {/* Where this item is used */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography variant="subtitle2" gutterBottom>Used in</Typography>
                    {!itemUsage ? (
                      <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    ) : (itemUsage.outfits.length === 0 && itemUsage.collections.length === 0 && itemUsage.fit_history.length === 0) ? (
                      <Typography variant="body2" color="text.secondary">
                        Not used in any outfits, collections, or logged fits yet.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {itemUsage.outfits.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Outfits</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {itemUsage.outfits.map((o) => (
                                <Chip key={o.id} label={o.name} size="small" onClick={() => navigate(`/outfits/${o.id}`)} clickable />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {itemUsage.collections.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Collections</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {itemUsage.collections.map((c) => (
                                <Chip key={c.id} label={c.name} size="small" onClick={() => navigate('/collections')} clickable />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {itemUsage.fit_history.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Fit of the Day</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
                              {itemUsage.fit_history.slice(0, 6).map((f) => (
                                f.image_url
                                  ? <Box key={f.id} component="img" src={f.image_url} alt={f.date}
                                      sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                                      onClick={() => navigate('/fit-of-the-day')} />
                                  : <Chip key={f.id} label={f.date} size="small" onClick={() => navigate('/fit-of-the-day')} clickable />
                              ))}
                              <Typography variant="caption" color="text.secondary">
                                worn {itemUsage.fit_history.length}x
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedItem && (
              <Box sx={{ mb: 3 }}>
                {/* Show the photo manager for EVERY item, even legacy ones
                    with an empty images[] array. If we have a legacy
                    image_url but no images entries, synthesize a single
                    seed tile so the user sees their existing photo
                    alongside the dropzone. On the first new upload the
                    backend promotes the legacy URL into images[0]. */}
                <ItemImageGallery
                  itemId={selectedItem.id}
                  images={
                    (selectedItem.images && selectedItem.images.length > 0)
                      ? selectedItem.images
                      : (selectedItem.image_url
                          ? [{
                              image_id: 'legacy',
                              s3_key: selectedItem.s3_key || '',
                              image_url: selectedItem.image_url,
                              is_primary: true,
                              uploaded_at: selectedItem.created_at,
                              order_index: 0,
                            }]
                          : [])
                  }
                  primaryImageId={
                    selectedItem.primary_image_id ||
                    (selectedItem.image_url && (!selectedItem.images || selectedItem.images.length === 0)
                      ? 'legacy' : undefined)
                  }
                  onImagesUpdate={(updatedImages, newPrimaryId) => {
                    // Update both edit form data and selected item
                    setEditFormData({
                      ...editFormData,
                      images: updatedImages,
                      primary_image_id: newPrimaryId,
                    });
                    if (selectedItem) {
                      setSelectedItem({
                        ...selectedItem,
                        images: updatedImages,
                        primary_image_id: newPrimaryId,
                      });
                    }
                    // Refresh the wardrobe list so the (potentially new)
                    // smart thumbnail shows up on the grid card.
                    fetchItems();
                  }}
                  editable={true}
                />
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editFormData.name || ''}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Brand"
                  value={editFormData.brand || ''}
                  onChange={(e) => handleEditFormChange('brand', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Category"
                  value={editFormData.category || ''}
                  onChange={(e) => handleEditFormChange('category', e.target.value)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Subcategory"
                  value={editFormData.subcategory || ''}
                  onChange={(e) => handleEditFormChange('subcategory', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Size"
                  value={editFormData.size || ''}
                  onChange={(e) => handleEditFormChange('size', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Fit"
                  value={editFormData.fit || ''}
                  onChange={(e) => handleEditFormChange('fit', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Style"
                  value={editFormData.style || ''}
                  onChange={(e) => handleEditFormChange('style', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Price"
                  value={editFormData.purchase_price ?? ''}
                  onChange={(e) => handleEditFormChange('purchase_price', e.target.value ? parseFloat(e.target.value) : undefined)}
                  type="number"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Colors (comma-separated)"
                  value={editFormData.colors?.join(', ') || ''}
                  onChange={(e) => handleEditFormChange('colors', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  helperText="Enter colors separated by commas"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Materials (comma-separated)"
                  value={editFormData.materials?.join(', ') || ''}
                  onChange={(e) => handleEditFormChange('materials', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  helperText="Enter materials separated by commas"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Seasons (comma-separated)"
                  value={editFormData.season?.join(', ') || ''}
                  onChange={(e) => handleEditFormChange('season', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  helperText="Enter seasons separated by commas (e.g., Spring, Summer)"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Occasions (comma-separated)"
                  value={editFormData.occasion?.join(', ') || ''}
                  onChange={(e) => handleEditFormChange('occasion', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  helperText="Enter occasions separated by commas (e.g., Casual, Formal)"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  value={editFormData.tags?.join(', ') || ''}
                  onChange={(e) => handleEditFormChange('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  helperText="Enter tags separated by commas"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description / AI Summary"
                  value={editFormData.description || ''}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  multiline
                  rows={3}
                  helperText="AI-generated summary of the item (editable)"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={editFormData.notes || ''}
                  onChange={(e) => handleEditFormChange('notes', e.target.value)}
                  multiline
                  rows={3}
                  helperText="Your personal notes about this item"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Care Instructions"
                  value={editFormData.care_instructions || ''}
                  onChange={(e) => handleEditFormChange('care_instructions', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <ImageLightbox
        open={lightboxOpen}
        images={lightboxImages}
        onClose={() => setLightboxOpen(false)}
      />
    </Container>
  );
};

export default Wardrobe;